import { styled, Tooltip } from "@monolith-forensics/monolith-ui";

const StyledIcon = styled.span`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border-radius: 4px;
  cursor: help;

  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }

  .badged-icon-badge {
    position: absolute;
    right: -2px;
    bottom: -2px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.palette.background.default};
    color: ${({ theme }) => theme.palette.primary.main};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.palette.divider};
    pointer-events: none;
  }
`;

/**
 * An icon with a small corner badge that explains itself on hover: a help
 * cursor, a light hover highlight, and `tooltip` as the hover content. Used
 * for the system managed rows in Case Files (ENG-2133), and generic so any
 * table cell can mark a row the same way. Moves to monolith-ui in ENG-2234.
 */
export const BadgedIcon: React.FC<{
  // The base icon, an <img> or an svg component.
  icon: React.ReactNode;
  // A small glyph, e.g. a lucide icon at size 8.
  badge: React.ReactNode;
  tooltip: React.ReactNode;
  // Accessible name for the icon, since its meaning is in the tooltip.
  label: string;
  className?: string;
  "data-testid"?: string;
}> = ({ icon, badge, tooltip, label, className, "data-testid": testId }) => {
  return (
    <Tooltip content={tooltip} side="top" hoverableContent delayDuration={150}>
      <StyledIcon
        className={className}
        role="img"
        aria-label={label}
        data-testid={testId}
      >
        {icon}
        <span className="badged-icon-badge">{badge}</span>
      </StyledIcon>
    </Tooltip>
  );
};

export default BadgedIcon;
